import { useState, useEffect } from 'react';
import { isBrowser } from '@utils';

/**
 * Returns current window.innerWidth, throttled with rAF to avoid running
 * setState on every resize tick.
 *
 * Note on SSR/hydration: returns 0 during SSR and on the very first render
 * pass on the client, then updates synchronously after mount. Consumers MUST
 * guard mobile detection with `windowWidth > 0 && windowWidth < BREAKPOINT`
 * to avoid flashing the mobile layout before hydration completes.
 */
export function useWindowWidth(): number {
  const [windowWidth, setWindowWidth] = useState<number>(0);

  useEffect(() => {
    if (!isBrowser()) return undefined;

    let frame = 0;
    const update = () => {
      setWindowWidth(window.innerWidth);
    };

    const onResize = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return windowWidth;
}
